// public/v3/src/lib/workflow.js — shared v3 workflow predicates.
//
// Consolidates the program-normalization, billing-target lookup, label,
// condition-label, and v2-faithful workflow-eligibility checks that every
// v3 page (time-logs, studio, onboard, …) currently re-implements. Mirrors
// the semantics in v2's ccm-compliance.js so v3 pages can be ported page-by-
// page without drifting from the production-baseline behavior.
//
// Pure functions only — no React, no DOM, no fetches. Safe to import from
// any page or sub-module.
//
// Module format: CommonJS (module.exports). Jest reads this file directly
// (no babel-jest preset-env installed), and esbuild interops CommonJS
// modules cleanly when consumed via `import { x } from './workflow'` from
// JSX pages — same pattern as lib/panel-review.js. If a preset-env
// transformer is later added, switch to `export function` to match the
// rest of public/v3/src/lib.
//
// v2 references:
//   - isWorkflowExcludedPatient → public/ccm-compliance.js:704-712
//   - getActivePatients         → public/ccm-compliance.js:726-739
//   - BILLING_TIME_TARGETS      → public/ccm-compliance.js:7830-7849
//                                (also exported by public/v3/src/lib/push-emr.js)

// v2 source: BILLING_TIME_TARGETS (ccm-compliance.js:7830-7849).

const { entryProgramFamily } = require('./bhi-billing');
//
// Inlined here (rather than re-exported from push-emr.js) so this module
// stays test-friendly under bare-Node module loading: push-emr.js pulls
// in `./api`, React-touching helpers, and other heavy chains that aren't
// resolvable outside of an esbuild bundle. The two definitions are kept
// byte-identical; a follow-up consolidation can collapse them once the
// callers have all migrated to `workflow.js`.
const BILLING_TIME_TARGETS = {
  '99490': 20, '99491': 30, '99487': 60,
  '99439': 20, '99437': 30, '99489': 30,
  'G0556': 0, 'G0557': 0, 'G0558': 0, 'G0570': 0,
  '99484': 20, '99492': 70, '99493': 60,
  'G2214': 30, 'G0568': 70, 'G0569': 60,
  '99494': 30,
  'G3002': 30, 'G3003': 15,
  '99424': 30, '99426': 30, '99425': 30, '99427': 30,
  '99457': 20, '98980': 20,
  'G0023': 60, 'G0140': 60, 'G0024': 30, 'G0146': 30,
};

// Canonical program string. v2 used `''` and `'none'` interchangeably to
// mean "not enrolled"; the rest of v3 should never have to remember which.
function normalizeProgram(program) {
  if (program == null) return '';
  const s = String(program).trim();
  if (s === '' || s.toLowerCase() === 'none') return '';
  return s.toUpperCase();
}

function isEnrolledProgram(program) {
  return normalizeProgram(program) !== '';
}

// Surface a human-readable label for a single condition entry. Matches
// `conditionLabel` in lib/ai-suggest.js so chronic-count and Time
// Logs/Studio row UIs agree on what counts as a labeled condition.
//
// Elation patient.conditions entries carry `description` + `icd10` +
// `conditionId`; v2's ccmChronicConditionCount reads `description ||
// conditionId`. Keep `label`/`name`/`code` as fallbacks for synthetic /
// non-Elation shapes.
function conditionLabel(c) {
  if (typeof c === 'string') return c.trim();
  if (c && typeof c === 'object') {
    return (c.description || c.label || c.name || c.code || c.conditionId || '').trim();
  }
  return '';
}

// Active patients have `status === 'active'`. Missing status defaults to
// active to match v2 (only the explicit non-active values exclude).
function isActivePatient(raw) {
  if (!raw || typeof raw !== 'object') return false;
  return (raw.status || 'active') === 'active';
}

function isConsentDeclined(raw) {
  return !!(raw && raw.consent === 'declined');
}

// v2 source: isWorkflowExcludedPatient (ccm-compliance.js:704-712).
// All three flags must be present AND match exactly. Any missing piece
// (no eligibility blob, wrong reason, tags not verified) → not excluded.
function isWorkflowExcluded(raw) {
  const eligibility = raw && raw.elationSyncEligibility;
  return !!(
    eligibility &&
    eligibility.excludedFromWorkflows === true &&
    eligibility.reason === 'no_patient_tags' &&
    eligibility.tagsVerified === true
  );
}

// v2 source: getActivePatients (ccm-compliance.js:726-739) — the
// active/declined/excluded gate, minus the consent-date cutoff (which is
// month-specific and lives at the caller).
function isWorkflowPatient(raw) {
  return isActivePatient(raw) && !isConsentDeclined(raw) && !isWorkflowExcluded(raw);
}

// Map program code → target minutes for the month.
//   - No program / 'none' → 0 (nothing to bill against).
//   - APCM tiers (G0556/G0557/G0558 and the literal 'APCM') → 0 (flat-fee,
//     no time threshold).
//   - Known CCM tier in BILLING_TIME_TARGETS → its table value.
//   - Enrolled-but-unknown program → 20 (default to base CCM target).
function targetForProgram(program) {
  const p = normalizeProgram(program);
  if (p === '') return 0;
  if (p === 'APCM') return 0;
  if (Object.prototype.hasOwnProperty.call(BILLING_TIME_TARGETS, p)) {
    return BILLING_TIME_TARGETS[p];
  }
  return 20;
}

// Human-readable label for the program chip. Mirrors v2's display strings.
//   - 'Not enrolled' for empty / 'none'.
//   - 'APCM · flat' for APCM (flat-fee tier).
//   - Known CCM tiers get the canonical "CCM · N min" form.
//   - Unknown enrolled programs fall back to "CCM · <code>".
function programLabelFor(program) {
  const p = normalizeProgram(program);
  if (p === '') return 'Not enrolled';
  if (p === 'APCM') return 'APCM · flat';
  if (p === '99490') return 'CCM · 20 min';
  if (p === '99491') return 'CCM · 30 min';
  if (p === '99487') return 'Complex CCM · 60 min';
  if (p === '99424') return 'PCM Physician · 30 min';
  if (p === '99426') return 'PCM Staff · 30 min';
  return `CCM · ${p}`;
}

// --- BHI predicates ----------------------------------------------------------
//
// BHI (Behavioral Health Integration) runs as a concurrent program slot
// alongside CCM/APCM (per docs/superpowers/specs/2026-05-21-bhi-v3-design.md).
// Enrollment is recorded at `billing.bhiProgram` (the BHI bill code: '99484'
// today; G0570 lives on the APCM month decision, not here). Activity-log
// entries carry a `program` tag ('ccm' | 'bhi'); legacy untagged entries
// default to 'ccm' so historical CCM minutes don't accidentally count toward
// BHI's monthly target.

// True iff the patient has an active BHI billing slot. Empty / missing
// `billing.bhiProgram` means "not enrolled in BHI", which is distinct from
// "not enrolled in any program at all" (that's `isEnrolledProgram`).
function isBhiEnrolledPatient(raw) {
  return !!(raw && raw.billing && raw.billing.bhiProgram);
}

// Target minutes for a BHI bill code. Reads from BILLING_TIME_TARGETS so
// G0570 (flat-fee APCM add-on) returns 0 and 99484 returns 20; unknown
// codes return 0 (no spurious target enforced).
function bhiTargetForProgram(code) {
  return BILLING_TIME_TARGETS[code] != null ? BILLING_TIME_TARGETS[code] : 0;
}

// Sum of activity-log minutes that land in the BHI family for the given
// month prefix (e.g. '2026-05'). Family is derived from the CPT code stored
// in `entry.program` via `entryProgramFamily` (single source of truth — see
// bhi-billing.js). CCM-tier entries and legacy untagged entries are treated
// as 'ccm' and never count toward BHI.
function bhiActivityMinutes(raw, monthStr) {
  if (!raw || !Array.isArray(raw.activityLog)) return 0;
  const prefix = monthStr || '';
  let sum = 0;
  for (const e of raw.activityLog) {
    if (!e || typeof e !== 'object') continue;
    if (!(e.date || '').startsWith(prefix)) continue;
    if (e.isConsolidatedNote) continue;
    if (entryProgramFamily(e) !== 'bhi') continue;
    sum += parseInt(e.duration, 10) || 0;
  }
  return sum;
}

// Sum of activity-log minutes that land in the CCM family for the given
// month prefix. Family is derived from the CPT code in `entry.program` via
// `entryProgramFamily` (single source of truth — see bhi-billing.js). Legacy
// untagged entries default to 'ccm' and count here. BHI-tagged entries are
// excluded. Synthetic consolidated entries (e.g., monthly-push markers) are
// always excluded to prevent double-counting.
function ccmActivityMinutes(raw, monthStr) {
  if (!raw || !Array.isArray(raw.activityLog)) return 0;
  const prefix = monthStr || '';
  let sum = 0;
  for (const e of raw.activityLog) {
    if (!e || typeof e !== 'object') continue;
    if (!(e.date || '').startsWith(prefix)) continue;
    if (e.isConsolidatedNote) continue;
    if (entryProgramFamily(e) !== 'ccm') continue;
    sum += parseInt(e.duration, 10) || 0;
  }
  return sum;
}

// Row-level "operational for BHI" predicate. Mirrors v2's CCM-operational
// gate but with the BHI-specific consent + assessment requirements that
// 99484 (and G0570) reimbursement assumes. Accepts either a row wrapper
// (`{ data: raw }`) or a raw patient blob directly.
function isBhiOperationalRow(row) {
  const data = row && row.data && typeof row.data === 'object' ? row.data : row;
  if (!isActivePatient(data)) return false;
  if (isWorkflowExcluded(data)) return false;
  if (!isBhiEnrolledPatient(data)) return false;
  const consentSigned = (data && data.bhiConsent && data.bhiConsent.signed) === true;
  if (!consentSigned) return false;
  const assessments = data && data.bhi && data.bhi.assessments;
  if (!Array.isArray(assessments) || assessments.length === 0) return false;
  return true;
}

// Studio operational predicate, program-aware. The Studio page is the
// billing-closeout surface; its admission gates are stricter than Time
// Logs' (Studio requires initial BHI assessment in addition to
// enrollment + consent for the BHI lens; CCM lens requires the same
// `consent === 'signed'` Studio always has). Mirrors the structure of
// `time-logs-bhi-predicate.js#operationalForFilter` but uses the
// audit-ready strict gates.
function studioOperationalForFilter(row, programFilter) {
  const data = row && row.data && typeof row.data === 'object' ? row.data : row;
  if (!data) return false;
  const ccmOp = isActivePatient(data)
    && isEnrolledProgram(data.billing && data.billing.program)
    && data.consent === 'signed'
    && !isWorkflowExcluded(data);
  const bhiOp = isBhiOperationalRow(row);
  if (programFilter === 'bhi') return bhiOp;
  if (programFilter === 'ccm') return ccmOp;
  // Pain lens: the pain-enrolled caseload. CPM has no standalone push lane —
  // it pairs onto the CCM/APCM bill — so Studio under the pain lens shows the
  // pain-enrolled patients' regular CCM/APCM cards.
  if (programFilter === 'pain') {
    const painV = String((data.billing || {}).painProgram || '').trim();
    return !!painV && painV.toLowerCase() !== 'none'
      && isActivePatient(data) && data.consent === 'signed' && !isWorkflowExcluded(data);
  }
  // 'all' or undefined → union
  return ccmOp || bhiOp;
}

// Per-program unenrolled predicates. CCM uses the surfaced `p.program` string
// (the result of normalizeProgram on billing.program). BHI uses
// `p.raw.billing.bhiProgram` directly — that's where Phase 4 (PR #110) puts
// the BHI CPT after enrollment.
function isUnenrolledCcm(p) {
  return !isEnrolledProgram(p && p.program);
}

function isUnenrolledBhi(p) {
  const billing = (p && p.raw && p.raw.billing) || {};
  return !billing.bhiProgram;
}

// Pain (CPM) enrollment lives at billing.painProgram ('G3002'); 'none' and
// blank both mean unenrolled, matching isPainEnrolledPatient (pain-billing.js).
function isUnenrolledPain(p) {
  const v = String(((p && p.raw && p.raw.billing) || {}).painProgram || '').trim();
  return !v || v.toLowerCase() === 'none';
}

// PIN enrollment lives at billing.navProgram ('G0023'); as with Pain, blank
// and the legacy sentinel 'none' both mean unenrolled.
function isUnenrolledPin(p) {
  const v = String(((p && p.raw && p.raw.billing) || {}).navProgram || '').trim();
  return !v || v.toLowerCase() === 'none';
}

// programFilter ∈ {'ccm','bhi','pain','pin','all', undefined}. The 'all' chrome view (and
// undefined / anything unrecognized) falls back to CCM-centric so the Onboard
// main list keeps today's shape when the admin isn't lensed on a specific
// program. See docs/superpowers/specs/2026-05-21-bhi-onboard-bulk-enroll-design.md §2.
function isUnenrolledFor(p, programFilter) {
  if (programFilter === 'bhi') return isUnenrolledBhi(p);
  if (programFilter === 'pain') return isUnenrolledPain(p);
  if (programFilter === 'pin') return isUnenrolledPin(p);
  return isUnenrolledCcm(p);
}

module.exports = {
  BILLING_TIME_TARGETS,
  normalizeProgram,
  isEnrolledProgram,
  isUnenrolledCcm,
  isUnenrolledBhi,
  isUnenrolledPain,
  isUnenrolledPin,
  isUnenrolledFor,
  conditionLabel,
  isActivePatient,
  isConsentDeclined,
  isWorkflowExcluded,
  isWorkflowPatient,
  targetForProgram,
  programLabelFor,
  isBhiEnrolledPatient,
  bhiTargetForProgram,
  bhiActivityMinutes,
  ccmActivityMinutes,
  isBhiOperationalRow,
  studioOperationalForFilter,
};
